// Guest Connection log — a running, append-many-times list of hospitality
// touchpoints. Replaces the older fixed 8-checkbox "Initial Contact" checklist
// on the guest profile. Any teacher / coordinator in the org can add an entry
// (Guest Connection is a shared org-wide track); the same preset can be logged
// multiple times (e.g. "Personal Invitation" three separate visits).
//
// Delete rules (enforced on the server too):
//   • Coordinators and org admins can delete any entry.
//   • The person who created the entry can delete their own anytime.
//
// Props:
//   scope    "coord" | "teacher" — controls which API path we hit.
//   guestId  UUID of the guest.
//   orgId    Optional — required when scope="coord" and the caller isn't the
//            guest's implicit org (site admin viewing another org).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Plus, X, Trash2, Sparkles } from "lucide-react";
import { formatDisplayName } from "@/lib/names";
import { fmtDate, todayIso } from "@/lib/tracks";

// Kept in sync with backend INITIAL_CONTACT_ITEMS. Order = display order.
const PRESETS = [
  "Send Personal Welcome Video",
  "Personal Follow-Up Text",
  "Remember Something About Them",
  "Introduce Them to Someone",
  "Second-Visit Recognition",
  "Small Personal Gesture",
  "Personal Invitation",
  "Ask About a Prayer Need",
];

export default function ConnectionLog({ scope, guestId, orgId }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(() => ({
    kind: PRESETS[0],
    custom_label: "",
    date: todayIso(),
    notes: "",
  }));
  const [saving, setSaving] = useState(false);

  const basePath = scope === "teacher" ? `/teacher/guests/${guestId}/connections` : `/coord/guests/${guestId}/connections`;
  const params = useMemo(() => (scope === "coord" && orgId ? { org_id: orgId } : {}), [scope, orgId]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(basePath, { params })
      .then((r) => setEntries(r.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [basePath, params]);

  useEffect(load, [load]);

  const submit = async () => {
    const chose = form.kind === "__other__";
    const payload = {
      kind: chose ? "other" : "preset",
      preset_label: chose ? null : form.kind,
      custom_label: chose ? (form.custom_label || "").trim() : null,
      date: form.date || todayIso(),
      notes: form.notes || "",
    };
    if (chose && !payload.custom_label) {
      toast.error("Add a short label for the custom connection.");
      return;
    }
    setSaving(true);
    try {
      await api.post(basePath, payload, { params });
      toast.success("Connection logged.");
      setForm({ kind: PRESETS[0], custom_label: "", date: todayIso(), notes: "" });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove "${labelFor(entry)}" from ${fmtDate(entry.date)}?`)) return;
    try {
      await api.delete(`${basePath}/${entry.id}`, { params });
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <section className="mb-8" data-testid="connection-log">
      <header className="mb-4 pb-2 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-3">
          <div className="shrink-0 flex items-center justify-center rounded-md" style={{ width: 32, height: 32, background: "var(--surface-hover)", color: "var(--ember)" }}>
            <Sparkles size={16} strokeWidth={1.75} />
          </div>
          <div>
            <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Shared · open to every teacher</div>
            <h3 className="font-serif-display text-2xl leading-none mt-1" style={{ color: "var(--ink)" }}>Guest Connection</h3>
            <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
              Log every hospitality touch. Same item can be logged multiple times.
            </p>
          </div>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} className="btn-primary" data-testid="connection-add-btn">
            <Plus size={14} /> Add new connection
          </button>
        )}
      </header>

      {adding && (
        <div className="p-4 rounded-md border mb-5 space-y-3" style={{ borderColor: "var(--border-subtle)", background: "var(--surface-hover)" }} data-testid="connection-add-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Type of connection *</span>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                data-testid="connection-add-kind"
                className="w-full px-3 py-2 text-sm"
              >
                {PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                <option value="__other__">Other (custom)…</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Date</span>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="connection-add-date"
                className="w-full px-3 py-2 text-sm"
              />
            </label>
          </div>
          {form.kind === "__other__" && (
            <label className="block">
              <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Custom label *</span>
              <input
                value={form.custom_label}
                onChange={(e) => setForm({ ...form, custom_label: e.target.value })}
                placeholder="e.g. Dropped off a meal"
                data-testid="connection-add-custom-label"
                className="w-full px-3 py-2 text-sm"
                maxLength={120}
              />
            </label>
          )}
          <label className="block">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Notes (optional)</span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything you want the team to know…"
              data-testid="connection-add-notes"
              className="w-full px-3 py-2 text-sm"
            />
          </label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setAdding(false)} className="btn-ghost" data-testid="connection-add-cancel">
              Cancel
            </button>
            <button onClick={submit} disabled={saving} className="btn-primary" data-testid="connection-add-save">
              {saving ? "Saving…" : "Log connection"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm italic" style={{ color: "var(--muted)" }} data-testid="connection-loading">Loading log…</p>
      ) : entries.length === 0 ? (
        <div className="p-6 rounded-md text-sm text-center" style={{ background: "var(--surface-hover)", border: "1px dashed var(--border-subtle)", color: "var(--ink-2)" }} data-testid="connection-empty">
          No connections logged yet. Add the first one to start tracking hospitality.
        </div>
      ) : (
        <ol className="space-y-3" data-testid="connection-entries">
          {entries.map((e) => {
            const canDelete =
              user?.is_admin ||
              user?.is_org_admin ||
              user?.is_coordinator ||
              e.by_user_id === user?.id;
            return (
              <li
                key={e.id}
                className="p-3 rounded-md border flex items-start gap-3"
                style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}
                data-testid={`connection-entry-${e.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: e.kind === "other" ? "var(--gold)" : "var(--ember)", color: "#FFFFFF" }}
                      data-testid={`connection-entry-label-${e.id}`}
                    >
                      {labelFor(e)}
                    </span>
                    <span className="text-xs" style={{ color: "var(--muted)" }}>{fmtDate(e.date)}</span>
                    {e.by_name && (
                      <span className="text-xs" style={{ color: "var(--muted)" }}>
                        · by {formatDisplayName({ name: e.by_name, gender: e.by_gender })}
                      </span>
                    )}
                  </div>
                  {e.notes && (
                    <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "var(--ink-2)" }} data-testid={`connection-entry-notes-${e.id}`}>
                      {e.notes}
                    </p>
                  )}
                </div>
                {canDelete && (
                  <button
                    onClick={() => remove(e)}
                    className="p-1.5 shrink-0"
                    style={{ color: "var(--terracotta)" }}
                    title="Remove this entry"
                    data-testid={`connection-entry-delete-${e.id}`}
                    aria-label="Delete connection entry"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function labelFor(entry) {
  if (entry.kind === "other") return entry.custom_label || "Other";
  return entry.preset_label || "Connection";
}
